/**
 * Sanitize CSS and scope it to a specific class
 * 
 * Supports these formats:
 * - Inline styles: {margin-top: 10px; border: 1px solid black;}
 * - Full CSS: .simple-text { color: red; }
 * - Multiple rules: .simple-text { color: red; } .another-class { background: blue; }
 * - Mixed inline + nested: {margin-top: 10px; color: red;} .another-class { background: blue; }
 */
export async function sanitizeCSS(css: string, className: string): Promise<string | null> {
  if (!css || typeof css !== 'string') {
    return null;
  }

  // Basic CSS sanitization - remove dangerous content before processing
  const sanitized = css
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
    .replace(/javascript:/gi, '')
    .replace(/expression\s*\([^)]*\)/gi, '') // Remove entire expression() function
    .replace(/url\s*\(\s*['"]?\s*javascript:/gi, '');

  // Check if this looks like inline styles (no selectors)
  if (!sanitized.includes('{') && !sanitized.includes('}')) {
    // Treat as inline styles - apply to the section class
    return `.${className} { ${sanitized} }`;
  }

  // Check if this looks like inline styles wrapped in braces (like {background: red;})
  if (sanitized.startsWith('{') && sanitized.endsWith('}')) {
    const content = sanitized.slice(1, -1).trim(); // Remove braces
    return `.${className} { ${content} }`;
  }

  // Handle mixed styles (inline + nested selectors)
  if (sanitized.startsWith('{') && sanitized.includes('}') && sanitized.includes('.')) {
    return parseMixedStyles(sanitized, className);
  }

  // Use clean-css only on server side
  const isServer = import.meta.server || typeof window === 'undefined' || process?.server;
  
  if (isServer) {
    return await processCSSWithCleanCSS(sanitized, className);
  } else {
    // Client-side fallback - simple scoping without optimization
    return scopeCSSSimple(sanitized, className);
  }
}

/**
 * Process CSS with clean-css (server-side only)
 */
async function processCSSWithCleanCSS(css: string, className: string): Promise<string | null> {
  try {
    // Import clean-css only on server side
    const CleanCSS = await import('clean-css');
    
    const cleanCSS = new CleanCSS.default({
      compatibility: 'ie9',
      level: {
        1: {
          all: true,
          normalizeUrls: false,
          removeEmpty: true,
          removeWhitespace: true,
          specialComments: 'none'
        },
        2: {
          all: true, // Enable all level 2 optimizations
          mergeAdjacentRules: true,
          mergeIntoShorthands: true,
          mergeMedia: true,
          mergeNonAdjacentRules: true,
          mergeSemantically: true,
          removeDuplicateFontRules: true,
          removeDuplicateMediaBlocks: true,
          removeDuplicateRules: true,
          removeEmpty: true,
          removeUnusedAtRules: false
        }
      }
      // Removed format option to enable minification
    });

    // Scope the CSS to our class
    const scopedCSS = scopeCSS(css, className);
    const result = cleanCSS.minify(scopedCSS);
    
    if (result.errors && result.errors.length > 0) {
      console.warn('CSS parsing errors:', result.errors);
    }
    
    return result.styles || null;
  } catch (error) {
    console.error('Error processing CSS with clean-css:', error);
    // Fallback to simple scoping
    return scopeCSSSimple(css, className);
  }
}

/**
 * Simple CSS scoping for client-side (fallback)
 */
function scopeCSSSimple(css: string, className: string): string {
  // Simple approach: just wrap everything in the class
  // This is much simpler and more reliable for client-side
  
  // If it looks like inline styles (no selectors), just apply to class
  if (!css.includes('{') || css.startsWith('{')) {
    const content = css.startsWith('{') ? css.slice(1, -1).trim() : css;
    return `.${className} { ${content} }`;
  }
  
  // For CSS with selectors, do basic scoping
  return css.replace(/([^{]+)\{([^}]+)\}/g, (match, selectors, declarations) => {
    const scopedSelectors = selectors.split(',').map((selector: string) => {
      const trimmed = selector.trim();
      if (trimmed.startsWith('@') || trimmed.includes(':')) {
        return trimmed; // Don't scope media queries or pseudo-selectors
      }
      return `.${className} ${trimmed}`;
    }).join(', ');
    
    return `${scopedSelectors} { ${declarations} }`;
  });
}

/**
 * Scope CSS selectors to a specific class
 */
function scopeCSS(css: string, className: string): string {
  // Handle media queries and other at-rules
  const atRuleRegex = /(@[^{]+)\{([^}]*(?:\{[^}]*\}[^}]*)*)\}/g;
  let scopedCSS = css.replace(atRuleRegex, (match, atRule, content) => {
    const scopedContent = scopeCSSContent(content, className);
    return `${atRule}{${scopedContent}}`;
  });

  // Handle regular CSS rules
  scopedCSS = scopeCSSContent(scopedCSS, className);
  
  return scopedCSS;
}

/**
 * Scope CSS content (rules within at-rules or regular rules)
 */
function scopeCSSContent(content: string, className: string): string {
  const ruleRegex = /([^{]+)\{([^}]+)\}/g;
  return content.replace(ruleRegex, (match, selectors, declarations) => {
    const scopedSelectors = scopeSelectors(selectors, className);
    return `${scopedSelectors}{${declarations}}`;
  });
}

/**
 * Scope CSS selectors to a specific class
 */
function scopeSelectors(selectors: string, className: string): string {
  return selectors.split(',').map(selector => {
    const trimmed = selector.trim();
    
    // Skip if already scoped to our class
    if (trimmed.includes(className)) {
      return trimmed;
    }
    
    // Handle pseudo-selectors and media queries
    if (trimmed.startsWith('@') || trimmed.includes(':')) {
      return trimmed;
    }
    
    // Scope the selector
    return `.${className} ${trimmed}`;
  }).join(', ');
}

/**
 * Parses mixed inline styles and nested selectors.
 * This function handles cases where a single declaration block contains both
 * inline styles (e.g., `{margin-top: 10px; color: red;}`) and nested selectors
 * (e.g., `.another-class { background: blue; }`).
 * It returns a string of scoped CSS rules.
 */
function parseMixedStyles(css: string, className: string): string {
  const rules: string[] = [];
  
  // Extract the inline part (everything between first { and first })
  const firstClosingBrace = css.indexOf('}');
  const inlinePart = css.substring(1, firstClosingBrace).trim();
  
  if (inlinePart) {
    rules.push(`.${className} { ${inlinePart} }`);
  }
  
  // Extract the remaining CSS rules
  const remainingCSS = css.substring(firstClosingBrace + 1).trim();
  if (remainingCSS) {
    const scopedRemaining = scopeCSS(remainingCSS, className);
    rules.push(scopedRemaining);
  }
  
  return rules.join('\n');
} 